import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { useAuth } from "@/hooks/useAuth";
import { useOwnedShelters } from "@/hooks/useOwnedShelters";
import {
  useShelterTeam,
  useShelterInvites,
  useInviteTeamMember,
  useCancelInvite,
  useRemoveTeamMember,
} from "@/hooks/useShelterTeam";
import {
  usePendingJoinRequests,
  useApproveJoinRequest,
  useRejectJoinRequest,
} from "@/hooks/useTeamJoinRequests";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { PanelBadge } from "@/components/panel/PanelBadge";
import { Loader2, UserPlus, X, ArrowLeft, Mail, Shield, Users, Check, MessageSquare } from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";
import { pl } from "date-fns/locale";

const Team = () => {
  const { user } = useAuth();
  const { data: shelters, isLoading: loadingShelters } = useOwnedShelters(user?.id);
  const shelter = shelters?.[0];

  const { data: members, isLoading: loadingMembers } = useShelterTeam(shelter?.id);
  const { data: invites, isLoading: loadingInvites } = useShelterInvites(shelter?.id);

  const myRow = useMemo(
    () => members?.find((m) => m.user_id === user?.id),
    [members, user?.id],
  );
  const isOwner = myRow?.role === "owner";

  const teamCount = (members?.length ?? 0) + (invites?.length ?? 0);
  const canInvite = isOwner && teamCount < 3;

  const [email, setEmail] = useState("");
  const inviteMut = useInviteTeamMember();
  const cancelMut = useCancelInvite();
  const removeMut = useRemoveTeamMember();
  const { data: joinRequests } = usePendingJoinRequests(shelter?.id);
  const approveJoin = useApproveJoinRequest();
  const rejectJoin = useRejectJoinRequest();

  const handleInvite = async () => {
    if (!shelter) return;
    const cleaned = email.trim().toLowerCase();
    if (!cleaned || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(cleaned)) {
      toast.error("Podaj poprawny adres email");
      return;
    }
    try {
      await inviteMut.mutateAsync({ shelterId: shelter.id, email: cleaned });
      toast.success(`Wysłano zaproszenie na ${cleaned}`);
      setEmail("");
    } catch (e: any) {
      toast.error(e?.message ?? "Nie udało się wysłać zaproszenia");
    }
  };

  if (loadingShelters || loadingMembers) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!shelter) {
    return (
      <div className="max-w-2xl space-y-3">
        <h1 className="text-lg sm:text-2xl font-bold tracking-tight">Zespół</h1>
        <p className="text-xs sm:text-sm text-muted-foreground">
          Nie zarządzasz jeszcze żadnym schroniskiem.
        </p>
        <Button asChild variant="outline" size="sm">
          <Link to="/panel">Wróć do pulpitu</Link>
        </Button>
      </div>
    );
  }

  if (!isOwner) {
    const orphaned = shelter.has_owner === false;
    return (
      <div className="max-w-2xl space-y-4">
        <Card>
          <CardHeader className="p-3 sm:p-5 pb-2 sm:pb-3">
            <CardTitle className="text-sm sm:text-base">Zespół</CardTitle>
            <CardDescription>
              {orphaned
                ? `Schronisko "${shelter.name}" nie ma obecnie opiekuna profilu. Możesz złożyć wniosek o przejęcie tej roli lub opuścić zespół.`
                : `Jesteś członkiem zespołu schroniska "${shelter.name}". Zarządzanie zespołem należy do opiekuna profilu.`}
            </CardDescription>
          </CardHeader>
          <CardContent className="p-3 sm:p-5 pt-0 sm:pt-0 space-y-4">
            {orphaned && (
              <div className="rounded-md border border-primary/40 bg-primary/5 p-3 space-y-2">
                <p className="text-xs sm:text-sm">
                  Po zatwierdzeniu wniosku przez administratora zostaniesz opiekunem profilu i będziesz mógł zapraszać kolejne osoby do zespołu.
                </p>
                <Button asChild size="sm">
                  <Link to={`/panel/przejmij?shelter=${shelter.id}`}>
                    <Shield className="h-4 w-4 mr-2" />
                    Zostań opiekunem profilu
                  </Link>
                </Button>
              </div>
            )}
            <div className="border-t pt-3 space-y-2">
              <p className="text-xs text-muted-foreground">
                Jeśli nie chcesz już należeć do zespołu, możesz samodzielnie opuścić to schronisko. Stracisz dostęp do panelu i edycji danych.
              </p>
              <Button
                variant="outline"
                size="sm"
                disabled={removeMut.isPending}
                onClick={async () => {
                  if (!user) return;
                  if (!confirm(`Na pewno chcesz opuścić zespół schroniska "${shelter.name}"? Stracisz dostęp do panelu.`)) return;
                  try {
                    await removeMut.mutateAsync({ userId: user.id, shelterId: shelter.id });
                    toast.success("Opuściłeś zespół");
                    window.location.href = "/panel";
                  } catch (e: any) {
                    toast.error(e?.message ?? "Nie udało się opuścić zespołu");
                  }
                }}
              >
                {removeMut.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <X className="h-4 w-4 mr-2" />}
                Opuść zespół
              </Button>
            </div>
            <p className="text-[11px] text-muted-foreground border-t pt-3">
              Coś nie działa lub potrzebujesz pomocy? Kliknij przycisk „Pomoc" w prawym dolnym rogu, aby napisać do nas.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-3xl space-y-2.5 sm:space-y-5">
      <div className="space-y-0.5">
        <h1 className="text-lg sm:text-2xl font-bold tracking-tight">Zespół schroniska</h1>
        <p className="text-xs sm:text-sm text-muted-foreground">
          Schronisko może mieć do 3 osób w zespole. Każdy członek może edytować dane i potrzeby. Tylko opiekun profilu może zapraszać i usuwać członków zespołu.
        </p>
      </div>
      <Card>
        <CardHeader className="p-3 sm:p-5 pb-2 sm:pb-3">
          <CardTitle className="flex items-center gap-2 text-sm sm:text-base">
            <Users className="h-5 w-5 text-primary" />
            Członkowie i zaproszenia
          </CardTitle>
        </CardHeader>
        <CardContent className="p-3 sm:p-5 pt-0 sm:pt-0 space-y-4">
          <div className="space-y-2">
            <p className="text-xs uppercase tracking-wide text-muted-foreground font-medium">
              Aktualni członkowie ({members?.length ?? 0}/3)
            </p>
            <div className="space-y-2">
              {members?.map((m) => (
                <div
                  key={m.user_id}
                  className="flex items-center justify-between rounded-md border p-3 bg-muted/30"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                      <Shield className="h-4 w-4 text-primary" />
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium truncate">
                          {m.user_id === user?.id
                            ? "Ty"
                            : m.email ?? m.user_id.slice(0, 8) + "..."}
                        </span>
                        {m.role === "owner" ? (
                          <PanelBadge variant="verified" tooltip="Opiekun profilu - może zapraszać i usuwać członków zespołu">
                            Opiekun profilu
                          </PanelBadge>
                        ) : (
                          <PanelBadge variant="neutral" tooltip="Członek zespołu - może edytować profil i potrzeby">
                            Członek
                          </PanelBadge>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Dołączył: {format(new Date(m.granted_at), "d MMM yyyy", { locale: pl })}
                      </p>
                    </div>
                  </div>
                  {m.role === "member" && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={async () => {
                        if (!confirm(`Usunąć ten dostęp z zespołu?`)) return;
                        try {
                          await removeMut.mutateAsync({ userId: m.user_id, shelterId: shelter.id });
                          toast.success("Usunięto z zespołu");
                        } catch (e: any) {
                          toast.error(e?.message ?? "Nie udało się usunąć");
                        }
                      }}
                    >
                      <X className="h-4 w-4" /> Usuń
                    </Button>
                  )}
                </div>
              ))}
            </div>
          </div>

          {!loadingInvites && invites && invites.length > 0 && (
            <div className="space-y-2">
              <p className="text-xs uppercase tracking-wide text-muted-foreground font-medium">
                Oczekujące zaproszenia ({invites.length})
              </p>
              <div className="space-y-2">
                {invites.map((inv) => (
                  <div
                    key={inv.id}
                    className="flex items-center justify-between rounded-md border border-dashed p-3"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <Mail className="h-4 w-4 text-muted-foreground shrink-0" />
                      <div className="min-w-0">
                        <p className="text-sm font-medium truncate">{inv.invited_email}</p>
                        <p className="text-xs text-muted-foreground">
                          Wygasa: {format(new Date(inv.expires_at), "d MMM yyyy", { locale: pl })}
                        </p>
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={async () => {
                        try {
                          await cancelMut.mutateAsync({ inviteId: inv.id, shelterId: shelter.id });
                          toast.success("Zaproszenie anulowane");
                        } catch (e: any) {
                          toast.error(e?.message ?? "Nie udało się anulować");
                        }
                      }}
                    >
                      Anuluj
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {joinRequests && joinRequests.length > 0 && (
            <div className="space-y-2">
              <p className="text-xs uppercase tracking-wide text-muted-foreground font-medium flex items-center gap-1.5">
                <MessageSquare className="h-3.5 w-3.5 text-primary" />
                Prośby o dołączenie do zespołu ({joinRequests.length})
              </p>
              <div className="space-y-2">
                {joinRequests.map((req) => {
                  const limitReached = teamCount >= 3;
                  return (
                    <div
                      key={req.id}
                      className="rounded-md border border-primary/30 bg-primary/5 p-3 space-y-2"
                    >
                      <div className="flex items-start gap-2">
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium truncate">{req.requester_email}</p>
                          <p className="text-[11px] text-muted-foreground">
                            Prośba z {format(new Date(req.created_at), "d MMM yyyy, HH:mm", { locale: pl })}
                          </p>
                        </div>
                      </div>
                      <p className="text-xs text-foreground/90 whitespace-pre-wrap bg-background/70 rounded p-2 border">
                        {req.message}
                      </p>
                      {limitReached && (
                        <p className="text-[11px] text-orange-700 dark:text-orange-300">
                          Limit 3 osób w zespole został osiągnięty - aby zaakceptować, najpierw zwolnij miejsce.
                        </p>
                      )}
                      <div className="flex flex-wrap gap-2">
                        <Button
                          size="sm"
                          disabled={approveJoin.isPending || limitReached}
                          onClick={async () => {
                            try {
                              await approveJoin.mutateAsync({
                                requestId: req.id,
                                shelterId: shelter.id,
                                requesterEmail: req.requester_email,
                              });
                              toast.success(`Wysłano zaproszenie do ${req.requester_email}`);
                            } catch (e: any) {
                              toast.error(e?.message ?? "Nie udało się zaakceptować");
                            }
                          }}
                        >
                          {approveJoin.isPending ? (
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          ) : (
                            <Check className="h-4 w-4 mr-2" />
                          )}
                          Zaakceptuj i wyślij zaproszenie
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          disabled={rejectJoin.isPending}
                          onClick={async () => {
                            if (!confirm("Odrzucić tę prośbę?")) return;
                            try {
                              await rejectJoin.mutateAsync({ requestId: req.id, shelterId: shelter.id });
                              toast.success("Prośba odrzucona");
                            } catch (e: any) {
                              toast.error(e?.message ?? "Nie udało się odrzucić");
                            }
                          }}
                        >
                          <X className="h-4 w-4 mr-1" /> Odrzuć
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <div className="border-t pt-4 space-y-2">
            <Label htmlFor="invite-email" className="flex items-center gap-1">
              <UserPlus className="h-4 w-4" /> Zaproś nową osobę
            </Label>
            {canInvite ? (
              <div className="flex flex-col sm:flex-row gap-2">
                <Input
                  id="invite-email"
                  type="email"
                  placeholder="email@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={inviteMut.isPending}
                />
                <Button
                  onClick={handleInvite}
                  disabled={inviteMut.isPending || email.trim().length === 0}
                >
                  {inviteMut.isPending ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : (
                    <UserPlus className="h-4 w-4 mr-2" />
                  )}
                  Wyślij zaproszenie
                </Button>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">
                Limit 3 osób w zespole został osiągnięty. Aby zaprosić kolejną, najpierw usuń członka lub anuluj zaproszenie.
              </p>
            )}
            <p className="text-xs text-muted-foreground">
              Zaproszony otrzyma email z linkiem aktywacyjnym (ważny 14 dni). Po zalogowaniu na ten adres dołączy do zespołu jako członek z prawem edycji.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Team;
